import React from 'react';
import ReservationStepCheck from 'src/containers/events/reservation/stepCheck';
import ReservationStepFillData from 'src/containers/events/reservation/stepFillData';
import ReservationStepSelectAService from 'src/containers/events/reservation/selectAService';
import ReservationSummary from 'src/containers/events/reservation/summary';
import queryString from 'query-string';
import { changeDocTitle, sortObjects, normalizeSlugname } from 'src/components/helpers';
import EventService from 'src/services/api/eventService';
import moment from 'moment';
import Swal from 'sweetalert2';
import { sendReservationHelper } from 'src/containers/events/reservation/components/reservationExtraInfo';
import {
  fetchAuditoriumInscription,
  mergeAuditoriumInscriptionInfo,
} from 'src/containers/events/reservation/auditoriumInscription';
import {
  isCaptchaRequired,
  useRecaptchaToken,
  RECAPTCHA_SECURITY_ERROR,
} from 'src/components/helpers/recaptchaUtils';

export const spouseGender = (gender) => (gender === 'Masculino' ? 'ella' : 'el');

const ServiceReservation = (props) => {
  const [Step, setStep] = React.useState('check');
  const [inputs, setInputs] = React.useState({});
  const [loading, set_loading] = React.useState(false);
  const [showSummaryForm, set_showSummaryForm] = React.useState(false);
  const [services, set_services] = React.useState({});
  const getCaptchaToken = useRecaptchaToken('event_reservation');
  const getCheckCaptchaToken = useRecaptchaToken('event_check');

  let query = props.location.search;
  let parsedQueryString = queryString.parse(query);

  const defaulteventSlugname = () => {
    const { event, eventSlugname } = parsedQueryString;
    if (event) return normalizeSlugname(event);
    if (eventSlugname) return normalizeSlugname(eventSlugname);
    return 'Auditorio_Principal';
  };

  const firstEventOfQuery = Object.values(services)?.[0]?.[0];

  React.useEffect(() => {
    const getDate = (date) => moment.tz(date, 'America/Bogota').format('YYYY-MM-DD');
    set_loading(true);

    let eventSlugname = normalizeSlugname(inputs.eventSlugname) || defaulteventSlugname();
    props.history.push(`/servicio/reservacion?event=${eventSlugname}&id=event`);

    let body = { eventSlugname, currentWeek: true };
    EventService.getAuditoriumEvents(body)
      .then((response) => {
        if (response.data.success) {
          set_loading(false);
          let events = {};
          sortObjects(response.data.events, 'date', 'asc').forEach((event) => {
            events[getDate(event.date)] = [...(events[getDate(event.date)] || []), event];
          });
          set_services(events);
        }
      })
      .catch((err) => {
        set_loading(false);
      });
  }, [inputs.eventSlugname]);

  const sendReservation = async ({
    goToSummary = true,
    redirect = '',
    eventSelected = {},
    inputsOverride,
  }) => {
    const effectiveInputs = inputsOverride ?? inputs;
    set_loading(true);

    const captchaRequired = isCaptchaRequired();
    const captcha = await getCaptchaToken({ skip: !captchaRequired });
    if (captchaRequired && !captcha) {
      set_loading(false);
      Swal.fire(RECAPTCHA_SECURITY_ERROR);
      return;
    }

    sendReservationHelper({
      inputs: {
        ...effectiveInputs,
        ...(captchaRequired ? { captcha } : {}),
      },
      event: firstEventOfQuery,
      eventSlugname: defaulteventSlugname(),
      special: parsedQueryString.special,
      eventSelected,
    })
      .then((response) => {
        let resp = {};
        set_loading(false);
        if (response.data.success) {
          if (response.data.created) {
            if (response.data.reserved) {
              resp = { ...resp, reserved: response.data.reserved, ...response.data.reserved };
              if (response.data.allReservations) {
                resp = { ...resp, allReservations: response.data.allReservations };
              }
            }
            setInputs({ ...effectiveInputs, ...resp });

            if (redirect) {
              const reservationIds =
                response.data?.allReservations
                  ?.map?.((r) => r['AuditoriumGuests.id'])
                  ?.join?.(',') ||
                response.data.reserved?.['AuditoriumGuests.id'] ||
                response.data.reserved?.id;

              const updatedRedirect = redirect + `&extra2=[${reservationIds}]`;
              props.history.push(updatedRedirect);
            } else if (goToSummary) {
              setStep('summary');
            }
          }
        } else {
          if (response.data) {
            Swal.fire(response.data.message, response?.data?.submessage || '', 'error');
          }
        }
      })
      .catch((err) => {
        set_loading(false);
      });
  };

  const isAudPpal = defaulteventSlugname() === 'Auditorio_Principal';

  React.useEffect(() => {
    changeDocTitle('Reserva tu lugar');
  }, []);

  React.useEffect(() => {
    const {
      parentIdentification,
      identification,
      parentFullname,
      eventSlugname,
      parentPhone,
      phone,
      event,
    } = parsedQueryString;
    setInputs({
      ...inputs,
      emergencyContact: parentFullname,
      emergencyNumber: parentPhone,
      eventSlugname: eventSlugname || event,
      parentFullname,
      parentIdentification,
      identification,
      phone,
    });
  }, []);

  const checkAuditoriumGuest = async ({
    identification = inputs.identification,
    phone = inputs.phone,
    updateStep = false,
  } = {}) => {
    set_loading(true);
    const captchaRequired = isCaptchaRequired();
    const captcha = await getCheckCaptchaToken({ skip: !captchaRequired });
    if (captchaRequired && !captcha) {
      set_loading(false);
      Swal.fire(RECAPTCHA_SECURITY_ERROR);
      return;
    }

    try {
      const { response, lookupPhone } = await fetchAuditoriumInscription({
        inputs,
        identification,
        phone,
        eventSlugname: defaulteventSlugname(),
        captcha,
        captchaRequired,
      });
      if (response.data.success) {
        const info = mergeAuditoriumInscriptionInfo(response.data, {
          identification,
          phone: lookupPhone,
        });
        setInputs((prev) => ({
          ...prev,
          ...info,
          identification,
          phone: lookupPhone,
        }));
        if (updateStep) {
          setStep(response.data?.reserved?.id ? 'summary' : 'FillData');
        }
      }
    } catch (err) {
      // Keep the current step even if lookup fails after a successful save.
    } finally {
      set_loading(false);
    }
  };

  const stepRender = () => {
    if (Step === 'check') {
      return (
        <ReservationStepCheck
          setInputs={setInputs}
          inputs={inputs}
          isAudPpal={isAudPpal}
          set_loading={set_loading}
          loading={loading}
          setStep={setStep}
          services={services}
          history={props.history}
          parsedQueryString={parsedQueryString}
          defaulteventSlugname={defaulteventSlugname}
          checkAuditoriumGuest={checkAuditoriumGuest}
        />
      );
    } else if (Step === 'FillData') {
      return (
        <ReservationStepFillData
          setInputs={setInputs}
          parsedQueryString={parsedQueryString}
          firstEventOfQuery={firstEventOfQuery}
          setStep={setStep}
          history={props.history}
          services={services}
          inputs={inputs}
          loading={loading}
          sendReservation={sendReservation}
          defaulteventSlugname={defaulteventSlugname}
          onGuestUpdated={checkAuditoriumGuest}
        />
      );
    } else if (Step === 'selectAService') {
      return (
        <ReservationStepSelectAService
          sendReservation={sendReservation}
          setInputs={setInputs}
          services={services}
          setStep={setStep}
          inputs={inputs}
          set_loading={set_loading}
          loading={loading}
          isAudPpal={isAudPpal}
          defaulteventSlugname={defaulteventSlugname}
          parsedQueryString={parsedQueryString}
          set_showSummaryForm={set_showSummaryForm}
          firstEventOfQuery={firstEventOfQuery}
        />
      );
    } else if (Step === 'summary') {
      return (
        <ReservationSummary
          setInputs={setInputs}
          setStep={setStep}
          history={props.history}
          inputs={inputs}
          isAudPpal={isAudPpal}
          set_showSummaryForm={set_showSummaryForm}
          showSummaryForm={showSummaryForm}
          defaulteventSlugname={defaulteventSlugname}
          parsedQueryString={parsedQueryString}
        />
      );
    }
  };

  return <div>{stepRender()}</div>;
};

export default ServiceReservation;
